import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { Build, Step } from "~/components/build/Show/lib/types";
import {
  allStepsQueryOptions,
  stepsByStateQueryOptions,
  topLevelStepsQueryOptions,
} from "~/components/build/Show/lib/queries/steps";
import { FilterByOption } from "~/components/build/Show/lib/useFilterStore";

const FAKE_USE_QUERY_RESPONSE = {
  isLoading: false,
  data: [] as Step[],
  refetch: () => Promise.resolve(),
};

/**
 * Fetch **all** the steps for a build.
 */
export function useStepsQuery(
  build: Build,
  options?: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn">,
) {
  const query = useQuery(
    allStepsQueryOptions(build, {
      ...options,
      enabled: Features.ProgressiveBuildShow && options?.enabled !== false,
    }),
  );

  if (!Features.ProgressiveBuildShow) {
    return FAKE_USE_QUERY_RESPONSE;
  }

  return query;
}

/**
 * Fetch (ungrouped) steps for a build filtered by state.
 */
export const useStepsForStateQuery = (
  build: Build,
  state: FilterByOption,
  options?: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn">,
) => {
  const query = useQuery(
    stepsByStateQueryOptions(build, state, {
      ...options,
      enabled: Features.ProgressiveBuildShow && options?.enabled !== false,
    }),
  );

  if (!Features.ProgressiveBuildShow) {
    return FAKE_USE_QUERY_RESPONSE;
  }

  return query;
};

/**
 * Fetch **top-level** steps for a build (i.e group steps and ungrouped steps)
 */
export function useTopLevelStepsQuery(
  build: Build,
  options?: Omit<UseQueryOptions<Step[]>, "queryKey" | "queryFn">,
) {
  const query = useQuery(
    topLevelStepsQueryOptions(build, {
      ...options,
      enabled: Features.ProgressiveBuildShow && options?.enabled !== false,
    }),
  );

  if (!Features.ProgressiveBuildShow) {
    return FAKE_USE_QUERY_RESPONSE;
  }

  return query;
}

/**
 * Get steps from the build store.
 *
 * The store is updated by the BuildContext.
 */
export function useSteps() {
  const steps = useBuildStore((state) => state.steps);
  const stepsMap = useBuildStore((state) => state.stepsMap);
  const groupStepsMap = useBuildStore((state) => state.stepsGroupedByParent);
  const stepJobs = useBuildStore((state) => state.stepJobs);

  return {
    steps,
    stepsMap,
    groupStepsMap,
    stepJobs,
  };
}
